import { InboundMessageType, OutboundMessageType, parseOutboundMessage, PrefillDataType } from "./messages";
import type {
    InboundMessage,
    OutboundMessage,
    OutboundMessageMap,
    PrefillInfoItem,
    SubmissionBody,
    SubmissionPrefill,
    TranscribeScannerRequestMessage,
    TranscribeScannerResultData,
    ValidationTarget,
} from "./messages";

/** Listener for a specific outbound message type. */
export type OutboundListener<T extends OutboundMessageType> = (message: OutboundMessageMap[T]) => void;

/** Listener for every outbound message (used by {@link CaptelloWebview.onAny}). */
export type AnyOutboundListener = (message: OutboundMessage) => void;

/**
 * Handler for {@link CaptelloWebview.onTranscribeScannerRequest}. Receives the full
 * request (`element_id`, `image_urls`, `draft_submission_token`) plus a
 * {@link TranscribeScannerReply}, and answers in either style:
 *
 * - **return** the transcribed fields (a promise is fine) — the promise-based style, or
 * - **call `reply.resolve(...)` / `reply.reject(...)`** whenever the work finishes —
 *   for callback-style code that can't hand back a promise.
 *
 * Returning nothing hands ownership to `reply`, leaving the request open until it
 * answers. A thrown error / rejection becomes an error result; if it's an `Error`, its
 * `message` is shown to the user by the webview, so throw display-ready messages.
 */
export type TranscribeScannerRequestHandler = (
    request: TranscribeScannerRequestMessage,
    reply: TranscribeScannerReply,
) => TranscribeScannerResultData | void | Promise<TranscribeScannerResultData | void>;

/**
 * The second argument handed to a {@link TranscribeScannerRequestHandler} — everything
 * needed to answer one request, so the handler never has to reach back out to the
 * client or touch `postMessage` itself.
 *
 * Use it when the transcription can't hand back a promise: a callback-style AJAX
 * wrapper, an event bus, a method that returns `void` and finishes whenever it
 * finishes. Pass it along to whatever does the work and let that code answer:
 *
 * ```js
 * webview.onTranscribeScannerRequest((request, reply) => {
 *   transcribeService(request.image_urls,
 *     (fields) => reply.resolve({ fields, submissionType: "ocr_transcription" }),
 *     () => reply.reject("Transcription failed."),
 *   );
 * });
 * ```
 *
 * A handler that returns a value (or a promise of one) is answered from that return
 * value and can ignore this entirely — the promise style is unchanged.
 *
 * The first answer wins: once `resolve`, `reject`, or the handler's own return value
 * has answered, later calls are ignored rather than posting a second result.
 */
export interface TranscribeScannerReply {
    /** Answer with the transcribed fields. */
    resolve(data: TranscribeScannerResultData): void;
    /**
     * Fail the request. `error` is shown to the user by the webview verbatim, so pass
     * display-ready text.
     */
    reject(error: string): void;
    /** `true` once this request has been answered, by either path. */
    readonly answered: boolean;
}

/**
 * Identifies the transcribe request being answered by
 * {@link CaptelloWebview.resolveTranscribeScannerRequest} /
 * {@link CaptelloWebview.rejectTranscribeScannerRequest} — the request message itself,
 * or its bare `request_id`.
 *
 * `null`/`undefined` sends an un-correlated result, which the webview accepts only
 * while exactly one request is pending. Pass the request whenever you have it.
 */
export type TranscribeScannerRequestRef = TranscribeScannerRequestMessage | string | null | undefined;

/** The `unsubscribe()` side of a subscription handle. */
export interface Subscription {
    /** Remove the listener. Safe to call more than once. */
    unsubscribe(): void;
}

/**
 * Handle returned by every `on*` method. It is an object carrying
 * {@link Subscription.unsubscribe}, and is also directly callable:
 *
 * ```ts
 * const sub = webview.on(OutboundMessageType.SubmissionBody, save);
 * sub.unsubscribe(); // preferred — reads clearly at the call site
 * sub();             // equivalent
 * ```
 *
 * Both do the same thing; the callable form keeps older `const off = ...; off()`
 * code working.
 */
export type Unsubscribe = (() => void) & Subscription;

/**
 * Rejection reason from {@link CaptelloWebview.submitAndWait} when the webview reports
 * a `form_error_message`, and from {@link CaptelloWebview.getSubmissionData} when its
 * result carries an `error`. `message` is the display-ready text.
 */
export class SubmissionError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "SubmissionError";
    }
}

/**
 * Rejection reason from {@link CaptelloWebview.submitAndWait} (and
 * {@link CaptelloWebview.getSubmissionData}) when the webview does not answer within the
 * timeout.
 */
export class SubmissionTimeoutError extends Error {
    constructor(
        public readonly timeoutMs: number,
        action = "submit",
    ) {
        super(`Captello webview did not respond to ${action} within ${timeoutMs}ms.`);
        this.name = "SubmissionTimeoutError";
    }
}

/** Options for {@link CaptelloWebview.submitAndWait}. */
export interface SubmitAndWaitOptions {
    /** How long to wait for the outcome before rejecting. Defaults to 60_000. */
    timeoutMs?: number;
    /**
     * Whether the webview also shows a refused submit (e.g. a validation error) as a toast
     * inside the form. The refusal reaches this call as a {@link SubmissionError} either
     * way, so pass `false` when you show the message yourself. Omit it to keep the
     * webview's default for the launcher: hidden for Event Gen, shown for the rest.
     */
    showErrorInForm?: boolean;
}

export interface CaptelloWebviewOptions {
    /**
     * Origin to validate incoming messages against and to target outgoing messages.
     * Strongly recommended — set it to the webview's origin (e.g.
     * `"https://capture.captello.com"`).
     *
     * A full URL is accepted and reduced to its origin, so you can pass the embed URL
     * or the webview base URL you already have on hand rather than deriving the origin
     * yourself.
     *
     * Defaults to `"*"`, which accepts messages from any origin and posts without an
     * origin check. Only acceptable for trusted/local development.
     */
    targetOrigin?: string;
    /**
     * The window to attach the `message` listener to. Defaults to the global `window`.
     * Override for testing or non-standard host environments.
     */
    hostWindow?: Window;
    /**
     * If `true` (default), incoming messages are accepted only when they originate
     * from the bound iframe's `contentWindow`. Set `false` only if the webview relays
     * messages through an intermediate window and source matching is impossible.
     */
    matchSource?: boolean;
    /**
     * If `true` (default), messages sent before the webview reports
     * `form_load_complete` are buffered and flushed, in order, once it's ready. This
     * removes a common footgun: calling `prefill(...)` right after mount would
     * otherwise post to a form that isn't listening yet and be silently dropped.
     *
     * Set `false` to send immediately (the legacy behavior). Note: a client that
     * attaches *after* the form already loaded will not have seen `form_load_complete`,
     * so its queued messages won't flush — create the client with the iframe.
     */
    queueUntilReady?: boolean;
    /**
     * If `true` (default), the client calls {@link CaptelloWebview.destroy} on itself
     * once the bound iframe is removed from the document — so a host that tears down a
     * panel, modal, or route doesn't leak the `message` listener or have to remember a
     * matching `destroy()`.
     *
     * Removal is detected with a `MutationObserver`, and an ancestor being removed
     * counts: what matters is that the iframe is no longer connected to the document.
     * Only ever fires after the iframe has been seen connected, so constructing against
     * a not-yet-inserted element is not mistaken for a teardown.
     *
     * Requires a real DOM element and `MutationObserver`; with a `{ contentWindow }`
     * stand-in, or in a non-DOM environment, there is nothing to observe and this is
     * inert. Set `false` to manage the lifetime entirely yourself — e.g. when you
     * deliberately detach and re-insert the same iframe element.
     */
    autoDestroy?: boolean;
    /**
     * If `true`, the client may learn its webview window from the **first** inbound
     * message instead of being handed an iframe — see {@link attach}, which is the
     * supported way to switch this on.
     *
     * Only ever adopts a sender that has already cleared the origin check and parsed as
     * a well-formed Captello message, so with `targetOrigin` set to the webview's origin
     * only the webview can be adopted. Every later message is source-matched against
     * whatever was adopted, so this is as strict as an explicit iframe after the first
     * message. Under the default `targetOrigin: "*"` that gate is absent — one more
     * reason to set it in production.
     *
     * An explicitly passed iframe always wins; nothing is adopted while one resolves.
     *
     * @default false
     */
    adoptSource?: boolean;
}

/**
 * Wraps a removal function into the {@link Unsubscribe} handle: callable, and carrying
 * an `unsubscribe()` method that does the same thing.
 */
function makeSubscription(off: () => void): Unsubscribe {
    const handle = (() => {
        off();
    }) as Unsubscribe;
    handle.unsubscribe = off;
    return handle;
}

/**
 * How often an adopted webview window is checked for `closed`. Long enough to be free
 * in practice, short enough that a closed panel doesn't leave a listener attached for
 * any meaningful time.
 */
const ADOPTED_WINDOW_POLL_MS = 5_000;

/** Numbers {@link CaptelloWebview.getSubmissionData} requests; see `nextRequestId`. */
let requestSeq = 0;

/** A correlation id unique to this page, and unlikely to collide with another client's. */
function nextRequestId(): string {
    requestSeq += 1;
    return `sd-${requestSeq}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Emits a console warning in development builds only. No-op in production / no bundler. */
function devWarn(message: string): void {
    try {
        if (typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production") {
            // eslint-disable-next-line no-console
            console.warn(message);
        }
    } catch {
        /* `process` not defined (pure browser, no bundler define) → stay silent */
    }
}

/** Options for {@link attach}. Same as the client's, minus the ones it manages itself. */
export type AttachOptions = Omit<CaptelloWebviewOptions, "adoptSource">;

/**
 * Attaches to the embedded Captello webview **without an iframe reference**.
 *
 * This is the drop-in replacement for a hand-rolled global
 * `window.addEventListener("message", …)` dispatcher. Call it once, register your
 * handlers, and you are done — there is nothing to query from the DOM, no ordering to
 * get right relative to when the iframe is created, and no teardown to remember.
 *
 * ```js
 * const webview = CaptelloSdk.attach({ targetOrigin: CAPTURE_PORTAL_WEB_VIEW_BASE });
 *
 * webview.on(CaptelloSdk.OutboundMessageType.FormErrorMessage, (m) => showError(m.data));
 * webview.on(CaptelloSdk.OutboundMessageType.SubmissionBody, (m) => save(m.data));
 * webview.onTranscribeScannerRequest((request, reply) => transcribe(request, reply));
 * ```
 *
 * How it finds the form: the first inbound message that clears the origin check **and**
 * parses as a well-formed Captello message identifies the webview. That sender becomes
 * the target for sends and the window every later message is source-matched against —
 * so after the first message this is as strict as passing the iframe. Set
 * `targetOrigin` to the webview's origin and only the webview can ever be adopted.
 *
 * Teardown is still automatic: a removed iframe's window reports `closed`, which is
 * readable cross-origin, so the client notices its form going away and destroys itself.
 * Pass `autoDestroy: false` to own the lifetime yourself.
 *
 * The trade-off versus {@link CaptelloWebview}: until the form speaks once there is no
 * window to post to, so an unprompted `submit()` / `prefill()` before then has nowhere
 * to go. With the default `queueUntilReady` those sends are buffered and flushed on
 * `form_load_complete`, which covers the normal case. If you drive the form
 * unprompted from page load and can hold the element, prefer
 * `new CaptelloWebview(iframe, …)`.
 */
export function attach(options: AttachOptions = {}): CaptelloWebview {
    // No element to bind: the window is filled in by adoption on the first message.
    return new CaptelloWebview({ contentWindow: null }, { ...options, adoptSource: true });
}

/**
 * Reduces a `targetOrigin` option to a bare origin.
 *
 * `postMessage` and the inbound `event.origin` check both work on origins only, but the
 * value a host has on hand is usually a full webview URL or a base path (e.g. the
 * `.../webview` base your embed URLs are built from). Accept either and derive the
 * origin, so passing a URL scopes messaging correctly instead of silently matching
 * nothing.
 */
function normalizeTargetOrigin(value: string | undefined): string {
    if (!value || value === "*") return "*";
    try {
        return new URL(value).origin;
    } catch {
        // Not parseable as a URL — pass through unchanged so an exact-origin string
        // still works in environments without a URL implementation.
        return value;
    }
}

/**
 * The webview's iframe — the element itself, or any object exposing its
 * `contentWindow` (useful for test doubles).
 *
 * The caller is responsible for the iframe being mounted before constructing the
 * client: messages are source-matched against its `contentWindow` from the very first
 * one, and sends post to it directly.
 *
 * When a real DOM element is passed, the client also tears itself down automatically
 * once that element leaves the document — see
 * {@link CaptelloWebviewOptions.autoDestroy}.
 */
export type FrameLike = HTMLIFrameElement | { contentWindow: Window | null };

/**
 * Host-side controller for an embedded Captello capture webview.
 *
 * Wraps a single `<iframe>` and encodes the full message protocol:
 * - **Receiving** (webview → host): subscribe with {@link on} / {@link onAny}.
 * - **Sending** (host → webview): use {@link submit}, {@link reset}, {@link prefill},
 *   {@link triggerValidation}, {@link updateDraft}, or the lower-level {@link send}.
 * - **Asking** (host → webview → host): {@link submitAndWait}, or
 *   {@link getSubmissionData} for the current values without validation.
 *
 * Wire details handled for you: outgoing messages are `JSON.stringify`'d (the webview
 * parses inbound data with `JSON.parse`, so a raw object would be ignored), and
 * incoming messages are validated by origin + source before being parsed.
 *
 * @example
 * ```ts
 * const iframe = document.querySelector("iframe")!;
 * const webview = new CaptelloWebview(iframe, {
 *   targetOrigin: "https://capture.captello.com",
 * });
 *
 * webview.on(OutboundMessageType.FormLoadComplete, () => console.log("ready"));
 * webview.on(OutboundMessageType.SubmissionBody, (msg) => save(msg.data));
 *
 * // later, drive the form:
 * webview.submit();
 *
 * // on teardown:
 * webview.destroy();
 * ```
 */
export class CaptelloWebview {
    private readonly frame: FrameLike;
    /** Watches for the iframe leaving the document — see `autoDestroy`. */
    private frameObserver?: MutationObserver;
    /** The webview window learned from an inbound message — see `adoptSource`. */
    private adoptedWindow: Window | null = null;
    private readonly adoptSource: boolean;
    private readonly autoDestroy: boolean;
    /** Watches an adopted window for `closed` — see `watchAdoptedWindow`. */
    private adoptedPoll?: ReturnType<typeof setInterval>;
    private readonly targetOrigin: string;
    private readonly hostWindow: Window;
    private readonly matchSource: boolean;

    private readonly listeners = new Map<OutboundMessageType, Set<OutboundListener<OutboundMessageType>>>();
    private readonly anyListeners = new Set<AnyOutboundListener>();
    private readonly boundHandler: (event: MessageEvent) => void;
    private destroyed = false;

    private readonly queueUntilReady: boolean;
    /** True once `form_load_complete` has been observed. */
    private ready = false;
    /** Messages sent before ready, flushed in order on load. */
    private readonly outbox: InboundMessage[] = [];

    constructor(frame: FrameLike, options: CaptelloWebviewOptions = {}) {
        if (!frame) {
            throw new Error("CaptelloWebview: a mounted iframe element (or { contentWindow }) is required.");
        }
        this.frame = frame;
        this.targetOrigin = normalizeTargetOrigin(options.targetOrigin);
        this.matchSource = options.matchSource ?? true;
        this.queueUntilReady = options.queueUntilReady ?? true;

        // Nudge (dev only) when running without origin scoping. "*" accepts inbound
        // messages from any origin and posts outbound without an origin check — fine
        // for local/trusted dev, unsafe in production. Set targetOrigin to the
        // webview's origin, e.g. `new URL(embedUrl).origin`.
        if (this.targetOrigin === "*") {
            devWarn(
                '[captello-sdk] No targetOrigin set — defaulting to "*", which accepts messages ' +
                    "from any origin and posts without an origin check. Set targetOrigin to the webview's " +
                    "origin (e.g. new URL(embedUrl).origin) in production.",
            );
        }

        const hostWindow = options.hostWindow ?? (typeof window !== "undefined" ? window : undefined);
        if (!hostWindow) {
            throw new Error(
                "CaptelloWebview: no host window available. Pass `hostWindow` when constructing outside a browser.",
            );
        }
        this.hostWindow = hostWindow;

        this.boundHandler = (event: MessageEvent) => this.handleMessage(event);
        this.hostWindow.addEventListener("message", this.boundHandler);

        this.adoptSource = options.adoptSource ?? false;
        this.autoDestroy = options.autoDestroy ?? true;
        if (this.autoDestroy) {
            this.watchForFrameRemoval();
        }
    }

    /**
     * Tears the client down once the bound iframe leaves the document.
     *
     * Observes the whole document subtree because the iframe usually goes away with an
     * ancestor (a panel or modal being emptied), which produces no mutation on the
     * iframe's own parent. `isConnected` is the actual test, so removal at any depth
     * counts; the observer only bothers checking when a mutation actually removed
     * something.
     */
    /**
     * Tears the client down once an adopted webview window goes away.
     *
     * With no iframe element there is nothing to observe in the DOM, but a removed
     * iframe's `contentWindow` reports `closed === true` — and `closed` is readable
     * cross-origin — so a cheap periodic check is enough. Verified in Chromium against a
     * genuinely cross-origin iframe.
     */
    private watchAdoptedWindow(): void {
        if (this.adoptedPoll !== undefined) return;
        this.adoptedPoll = setInterval(() => {
            let gone = false;
            try {
                gone = !this.adoptedWindow || this.adoptedWindow.closed;
            } catch {
                // Even `closed` can throw in exotic cases; treat that as gone rather
                // than looping on it forever.
                gone = true;
            }
            if (gone) this.destroy();
        }, ADOPTED_WINDOW_POLL_MS);
        // Don't hold a Node process open just for this (no-op in browsers).
        (this.adoptedPoll as unknown as { unref?: () => void })?.unref?.();
    }

    private watchForFrameRemoval(): void {
        const node = this.frame as Partial<Node>;
        // A `{ contentWindow }` stand-in, or an environment without the DOM APIs, has
        // nothing observable — leave the lifetime to the caller.
        if (typeof MutationObserver === "undefined" || typeof Node === "undefined" || !(this.frame instanceof Node)) {
            return;
        }
        const doc = node.ownerDocument;
        if (!doc?.documentElement) return;

        // Only arm once the iframe is actually in the document, so constructing against
        // an element that hasn't been inserted yet isn't read as a teardown.
        if (!node.isConnected) return;

        this.frameObserver = new MutationObserver((records) => {
            if (!records.some((record) => record.removedNodes.length > 0)) return;
            if (!node.isConnected) this.destroy();
        });
        this.frameObserver.observe(doc.documentElement, { childList: true, subtree: true });
    }

    /** `true` once the webview has reported `form_load_complete`. */
    get isReady(): boolean {
        return this.ready;
    }

    /* -------------------------------------------------------------- *
     * Receiving (webview → host)
     * -------------------------------------------------------------- */

    /**
     * Subscribe to a single outbound message type. Returns an unsubscribe function.
     *
     * @example webview.on(OutboundMessageType.FormErrorMessage, (m) => toast(m.data));
     */
    on<T extends OutboundMessageType>(type: T, listener: OutboundListener<T>): Unsubscribe {
        let set = this.listeners.get(type);
        if (!set) {
            set = new Set();
            this.listeners.set(type, set);
        }
        set.add(listener as OutboundListener<OutboundMessageType>);
        return makeSubscription(() => {
            set?.delete(listener as OutboundListener<OutboundMessageType>);
        });
    }

    /**
     * Subscribe once: the listener is removed automatically after it fires the first
     * time for `type`. Returns an unsubscribe function for cancelling early.
     */
    once<T extends OutboundMessageType>(type: T, listener: OutboundListener<T>): Unsubscribe {
        const off = this.on(type, (message) => {
            off();
            listener(message);
        });
        return off;
    }

    /** Subscribe to every outbound message regardless of type. Returns an unsubscribe function. */
    onAny(listener: AnyOutboundListener): Unsubscribe {
        this.anyListeners.add(listener);
        return makeSubscription(() => {
            this.anyListeners.delete(listener);
        });
    }

    /* -------------------------------------------------------------- *
     * Sending (host → webview)
     * -------------------------------------------------------------- */

    /**
     * Low-level send: posts any inbound message to the webview as a JSON string.
     * Prefer the typed helpers below; use this only for forward-compatibility.
     *
     * When `queueUntilReady` is enabled (the default) and the form hasn't reported
     * `form_load_complete` yet, the message is buffered and flushed on load instead of
     * posted immediately.
     *
     * @throws if the iframe's `contentWindow` is not available (not yet loaded /
     *   detached) and the message can't be queued.
     */
    send(message: InboundMessage): void {
        if (this.destroyed) {
            throw new Error("CaptelloWebview: cannot send after destroy().");
        }
        if (this.queueUntilReady && !this.ready) {
            this.outbox.push(message);
            return;
        }
        this.postNow(message);
    }

    /** Posts a message immediately, bypassing the ready-queue. */
    private postNow(message: InboundMessage): void {
        const target = this.frame.contentWindow ?? this.adoptedWindow;
        if (!target) {
            throw new Error(
                this.adoptSource
                    ? "CaptelloWebview: no webview window yet — nothing has been adopted, because no " +
                      "message has arrived from the form. Send only in response to a message, or after " +
                      "form_load_complete."
                    : "CaptelloWebview: iframe.contentWindow is null (not yet loaded, or detached). " +
                      "Construct the client with a mounted iframe.",
            );
        }
        // The webview reads inbound data with JSON.parse(event.data), so it must be a string.
        target.postMessage(JSON.stringify(message), this.targetOrigin);
    }

    /** Marks the client ready and flushes any queued messages, in order. */
    private markReadyAndFlush(): void {
        if (this.ready) return;
        this.ready = true;
        const queued = this.outbox.splice(0);
        for (const message of queued) {
            try {
                this.postNow(message);
            } catch {
                /* iframe detached between load and flush — drop silently */
            }
        }
    }

    /** Programmatically submit the form (fire-and-forget). */
    submit(): void {
        this.send({ type: InboundMessageType.Submit });
    }

    /**
     * Submit the form and await the outcome.
     *
     * Sends `submit_form`, then resolves with the {@link SubmissionBody} when the
     * webview emits `submission_body`, or rejects with a {@link SubmissionError}
     * (carrying the translated message) when it emits `form_error_message` — which it
     * does as soon as it refuses the submit, e.g. on an invalid email. Rejects with a
     * {@link SubmissionTimeoutError} if neither arrives within `timeoutMs`.
     *
     * This is the typed, leak-free version of the common "click submit, wait for the
     * result" flow — listeners are always cleaned up, including on timeout.
     *
     * @param options {@link SubmitAndWaitOptions}, or just the timeout in ms (default 60_000).
     * @example
     * try {
     *   // You show the error, so the form doesn't show it too.
     *   const body = await webview.submitAndWait({ showErrorInForm: false });
     *   await persist(body);
     * } catch (err) {
     *   if (err instanceof SubmissionError) showToast(err.message);
     * }
     */
    submitAndWait(options: number | SubmitAndWaitOptions = {}): Promise<SubmissionBody> {
        const { timeoutMs = 60_000, showErrorInForm } = typeof options === "number" ? { timeoutMs: options } : options;
        return new Promise<SubmissionBody>((resolve, reject) => {
            let settled = false;
            let timer: ReturnType<typeof setTimeout> | undefined;

            const cleanup = () => {
                settled = true;
                offSuccess();
                offError();
                if (timer !== undefined) clearTimeout(timer);
            };

            const offSuccess = this.on(OutboundMessageType.SubmissionBody, (message) => {
                if (settled) return;
                cleanup();
                resolve(message.data);
            });
            const offError = this.on(OutboundMessageType.FormErrorMessage, (message) => {
                if (settled) return;
                cleanup();
                reject(new SubmissionError(message.data));
            });

            if (timeoutMs > 0 && timeoutMs !== Infinity) {
                timer = setTimeout(() => {
                    if (settled) return;
                    cleanup();
                    reject(new SubmissionTimeoutError(timeoutMs));
                }, timeoutMs);
            }

            try {
                this.send(
                    showErrorInForm === undefined
                        ? { type: InboundMessageType.Submit }
                        : { type: InboundMessageType.Submit, show_error_in_form: showErrorInForm },
                );
            } catch (err) {
                if (!settled) {
                    cleanup();
                    reject(err);
                }
            }
        });
    }

    /**
     * Get the form's current values as a {@link SubmissionBody}, **without validating
     * them** — required fields may be empty, and nothing is checked, prompted, or saved.
     *
     * The body is built the way the webview builds `submission_body` on submit, so it can
     * be persisted the same way: pending files are uploaded first and arrive as URLs.
     * The form is left as it is; the user can keep editing and submit later.
     *
     * Unlike {@link submitAndWait}, this never emits `submission_body`, so an
     * `on(SubmissionBody, …)` handler that persists final submissions is not triggered.
     *
     * Resolves with the body, or rejects with a {@link SubmissionError} carrying the
     * webview's display-ready text (e.g. a file failed to upload), or a
     * {@link SubmissionTimeoutError} if no answer arrives within `timeoutMs` — which is
     * also what a webview build that predates `submission_data_request` produces. Sent
     * before the form loads, the request waits in the ready-queue like any other send.
     *
     * @param timeoutMs how long to wait for the answer. Defaults to 60_000.
     * @example
     * const body = await webview.getSubmissionData();
     * await saveProgress(body.data);
     */
    getSubmissionData(timeoutMs = 60_000): Promise<SubmissionBody> {
        const requestId = nextRequestId();
        return new Promise<SubmissionBody>((resolve, reject) => {
            let settled = false;
            let timer: ReturnType<typeof setTimeout> | undefined;

            const cleanup = () => {
                settled = true;
                off();
                if (timer !== undefined) clearTimeout(timer);
            };

            const off = this.on(OutboundMessageType.SubmissionDataResult, (message) => {
                if (settled || message.request_id !== requestId) return;
                cleanup();
                if (message.data) resolve(message.data);
                else reject(new SubmissionError(message.error || "Could not read the form's data."));
            });

            if (timeoutMs > 0 && timeoutMs !== Infinity) {
                timer = setTimeout(() => {
                    if (settled) return;
                    cleanup();
                    reject(new SubmissionTimeoutError(timeoutMs, "getSubmissionData"));
                }, timeoutMs);
            }

            try {
                this.send({ type: InboundMessageType.SubmissionDataRequest, request_id: requestId });
            } catch (err) {
                if (!settled) {
                    cleanup();
                    reject(err);
                }
            }
        });
    }

    /** Reset the form, clearing all entered values. */
    reset(): void {
        this.send({ type: InboundMessageType.Reset });
    }

    /** Switch the current submission into draft-update mode. */
    updateDraft(): void {
        this.send({ type: InboundMessageType.UpdateDraft });
    }

    /** Run validation against a target field, or `"all"` for the whole form. */
    triggerValidation(target: ValidationTarget): void {
        this.send({ type: InboundMessageType.TriggerValidation, target });
    }

    /** Pre-fill form fields from a submission body, transcription items, or both. */
    prefill(data: { submission?: SubmissionPrefill; info?: PrefillInfoItem[] }): void {
        this.send({
            type: InboundMessageType.FormPrefill,
            data_type: PrefillDataType.UlcSubmissionAndInfo,
            data,
        });
    }

    /**
     * Answer the webview's transcribe requests (its transcribe button — shown when the
     * embed URL sets `showTranscribeButton` — sends `transcribe_scanner_request` when
     * pressed).
     *
     * The handler receives the request (`element_id`, `image_urls`,
     * `draft_submission_token`) and a {@link TranscribeScannerReply}, and answers in
     * whichever style suits the transcription code. Either way the result is posted for
     * you — the host never builds or stringifies a `transcribe_scanner_result`.
     *
     * **Return the fields** when the work is promise-based:
     *
     * ```js
     * webview.onTranscribeScannerRequest(async ({ image_urls }) => {
     *   const fields = await transcribe(image_urls);
     *   return { fields, submissionType: "ocr_transcription" };
     * });
     * ```
     *
     * **Or answer through `reply`** when it isn't — a callback-style AJAX wrapper, an
     * event bus, a method that returns `void`. Hand `reply` to whatever does the work:
     *
     * ```js
     * webview.onTranscribeScannerRequest((request, reply) => {
     *   transcribeService(request.image_urls,
     *     (fields) => reply.resolve({ fields, submissionType: "ocr_transcription" }),
     *     () => reply.reject("Transcription failed."),
     *   );
     * });
     * ```
     *
     * A handler that returns nothing is taken to own the reply, so the request stays
     * open until `reply` answers it. A thrown error / rejected promise is sent as an
     * error result; an `Error`'s `message` is shown to the user by the webview, so throw
     * display-ready messages. The first answer wins — a second is ignored.
     *
     * Returns an unsubscribe function.
     */
    onTranscribeScannerRequest(handler: TranscribeScannerRequestHandler): Unsubscribe {
        return this.on(OutboundMessageType.TranscribeScannerRequest, (request) => {
            // One answer per request, whichever path gets there first: the reply handle,
            // or the handler's own return value.
            let answered = false;
            const answer = (result: { data?: TranscribeScannerResultData; error?: string }): void => {
                if (answered) {
                    devWarn(
                        "[captello-sdk] Ignored a second answer to transcribe request " +
                            `"${request.request_id}" — it was already answered. Answer either by returning ` +
                            "from the handler or by calling reply.resolve()/reply.reject(), not both.",
                    );
                    return;
                }
                answered = true;
                this.sendTranscribeScannerResult(request, result);
            };

            const reply: TranscribeScannerReply = {
                resolve: (data) => answer({ data }),
                reject: (error) => answer({ error: error || "Transcription failed." }),
                get answered() {
                    return answered;
                },
            };

            Promise.resolve()
                .then(() => handler(request, reply))
                .then(
                    (data) => {
                        // `undefined` means the handler didn't answer by returning, so it
                        // owns the reply — leave the request open. Answering later through
                        // `reply` is the normal deferred path, so this is not a problem...
                        if (data === undefined) {
                            // ...unless the handler never took a `reply` parameter, in
                            // which case nothing can ever answer and the form's button
                            // would spin until it times out. Almost always an async
                            // handler missing its `return`.
                            if (!answered && handler.length < 2) {
                                devWarn(
                                    "[captello-sdk] A transcribe handler returned nothing and takes no `reply` " +
                                        "parameter, so this request can never be answered and the form's button " +
                                        "will spin until it times out. Either return the fields, or accept " +
                                        "`(request, reply)` and call reply.resolve()/reply.reject().",
                                );
                            }
                            return;
                        }
                        answer({ data });
                    },
                    (err: unknown) =>
                        answer({
                            error: err instanceof Error && err.message ? err.message : "Transcription failed.",
                        }),
                );
        });
    }

    /**
     * Answer a transcribe request **later**, from code that can't hand back a promise
     * — a callback-style AJAX wrapper, an event bus, a method that returns `void`.
     *
     * Pair it with a plain `on(OutboundMessageType.TranscribeScannerRequest, ...)`
     * subscription: hold on to the request (or just its `request_id`), and call this
     * once the transcription lands. Prefer {@link onTranscribeScannerRequest} when
     * your transcription code is already promise-based — it does this for you.
     *
     * Accepts the request message itself or its bare `request_id`. The reply is
     * `JSON.stringify`'d and posted immediately, bypassing the ready-queue: a received
     * request already proves the webview is live and listening. Replying after
     * {@link destroy}, or after the iframe is detached, is a silent no-op — by then
     * there is nobody left to answer, and the webview times its own button out.
     *
     * @example
     * webview.on(OutboundMessageType.TranscribeScannerRequest, (request) => {
     *   ajax.send("Scanner", "transcribe", { image_urls: request.image_urls },
     *     (res) => webview.resolveTranscribeScannerRequest(request, {
     *       fields: res.fields,
     *       submissionType: "ocr_transcription",
     *     }),
     *     () => webview.rejectTranscribeScannerRequest(request, "Transcription failed."),
     *   );
     * });
     */
    resolveTranscribeScannerRequest(request: TranscribeScannerRequestRef, data: TranscribeScannerResultData): void {
        this.sendTranscribeScannerResult(request, { data });
    }

    /**
     * Fail a transcribe request answered via
     * {@link resolveTranscribeScannerRequest}'s deferred flow. `error` is shown to the
     * user verbatim by the webview, so pass display-ready text.
     *
     * Same delivery semantics as {@link resolveTranscribeScannerRequest}: posted
     * immediately, and a no-op once the client is destroyed or the iframe detached.
     */
    rejectTranscribeScannerRequest(request: TranscribeScannerRequestRef, error: string): void {
        this.sendTranscribeScannerResult(request, { error: error || "Transcription failed." });
    }

    /**
     * Posts a `transcribe_scanner_result`, bypassing the ready-queue. A received
     * request proves the webview is live and listening, even if this client attached
     * after form load and never saw `form_load_complete`; queuing here could stall the
     * reply forever while the webview's button sits in its pending state until timeout.
     */
    private sendTranscribeScannerResult(
        request: TranscribeScannerRequestRef,
        result: { data?: TranscribeScannerResultData; error?: string },
    ): void {
        if (this.destroyed) return;
        const requestId = typeof request === "string" ? request : request?.request_id;
        try {
            this.postNow({
                type: InboundMessageType.TranscribeScannerResult,
                // Dropped from the JSON when undefined; the webview accepts a result
                // without one while exactly one request is pending.
                request_id: requestId,
                ...result,
            });
        } catch {
            /* iframe detached while transcribing — nobody left to answer */
        }
    }

    /* -------------------------------------------------------------- *
     * Lifecycle
     * -------------------------------------------------------------- */

    /** Remove the `message` listener and drop all subscriptions. Idempotent. */
    destroy(): void {
        if (this.destroyed) return;
        this.destroyed = true;
        this.hostWindow.removeEventListener("message", this.boundHandler);
        this.listeners.clear();
        this.anyListeners.clear();
        this.outbox.length = 0;
        this.frameObserver?.disconnect();
        this.adoptedWindow = null;
        if (this.adoptedPoll !== undefined) {
            clearInterval(this.adoptedPoll);
            this.adoptedPoll = undefined;
        }
    }

    /* -------------------------------------------------------------- *
     * Internals
     * -------------------------------------------------------------- */

    private handleMessage(event: MessageEvent): void {
        if (this.destroyed) return;

        // Origin check: skip when targetOrigin is the wildcard. Warn (dev only) if a
        // message that *looks* like ours is dropped on origin — a common "why isn't my
        // listener firing?" cause.
        if (this.targetOrigin !== "*" && event.origin !== this.targetOrigin) {
            if (parseOutboundMessage(event.data)) {
                devWarn(
                    `[captello-sdk] Ignored a Captello message from origin "${event.origin}" ` +
                        `(expected "${this.targetOrigin}"). Check the targetOrigin you passed.`,
                );
            }
            return;
        }

        // Parse before the source check: only a genuine Captello message is worth
        // warning about, and only one is allowed to become the adopted source below.
        const message = parseOutboundMessage(event.data);
        if (!message) return;

        // Source check: only accept messages from the bound iframe's window (or, in
        // adopt mode, from whichever window was adopted first).
        if (this.matchSource) {
            const expected = this.frame.contentWindow ?? this.adoptedWindow;
            if (expected && event.source !== expected) {
                devWarn(
                    "[captello-sdk] Ignored a Captello message from an unexpected source window " +
                        "(not the bound iframe). If the webview relays through another window, set matchSource: false.",
                );
                return;
            }
        }

        // Adopt the sender as the webview, if we're in adopt mode and don't have one yet.
        // It has cleared the origin check and parsed as a real Captello message, so it is
        // the form — and it is where replies belong. Done before the ready-flush so
        // queued sends have somewhere to go.
        if (this.adoptSource && !this.adoptedWindow && !this.frame.contentWindow) {
            this.adoptedWindow = (event.source as Window | null) ?? null;
            if (this.adoptedWindow && this.autoDestroy) this.watchAdoptedWindow();
        }

        // Flip to ready (and flush queued sends) the moment the form loads, before
        // dispatching to listeners — so a listener can send and have it post immediately.
        if (message.type === OutboundMessageType.FormLoadComplete) {
            this.markReadyAndFlush();
        }

        const set = this.listeners.get(message.type);
        if (set) {
            // Copy to a snapshot so a listener that unsubscribes mid-dispatch is safe.
            for (const listener of [...set]) listener(message);
        }
        if (this.anyListeners.size) {
            for (const listener of [...this.anyListeners]) listener(message);
        }
    }
}
